import appConfig from '@/config/appConfig.typed';
import * as iccMethods from '@/consts/icc/iccMethods';
import {GET_TOKEN} from '@/consts/icc/iccMethods';
import * as logMessageTypes from '@/consts/log/logMessageTypes';
import authDataService from '@/services/auth/authDataService';
import authDataStoreService from '@/services/auth/authDataStoreService';
import authService from '@/services/auth/authService';
import iccResponseErrorCheckService from '@/services/icc/iccResponseErrorCheckService';
import {getMappedInitialLanguageCode} from '@/services/localization/localizationService';
import log from '@/services/logger/log';
import {logRequestError} from '@/services/logger/logService';
import {authMapping, authUamMapping, loginTokenListMapping, loginTokenMapping} from '@/services/mapping/iccMappings';
import productHelpers from '@/services/product/productHelpers';
import server from '@/services/server/server';
import storageService from '@/services/storage/storageService';
import {selectGigyaToken} from '@/state/selectors/auth';
import {makeSelectUamToken} from '@/state/selectors/uam';
import {addUamToken} from '@/state/slices/uamSlice';
import {dispatch, getState} from '@/state/store';
import urlUtils from '@/utils/urlUtils';

const getIccApiUrl = (methodName) => urlUtils.join(appConfig.getIccApiUrl(), methodName);

const getHeaders = (isAnonymous) => {
    const languageCode = getMappedInitialLanguageCode()?.toUpperCase();
    const l = languageCode || (isAnonymous ? 'EN' : undefined);

    return {
        a: appConfig.getIccApiHeader_a(),
        Accept: 'application/json',
        Authorization: appConfig.getIccApiHeaderAuthorization(),
        'Content-Type': 'application/x-www-form-urlencoded',
        l,
        m: appConfig.getIccMarketName()?.toUpperCase(),
        v: appConfig.getAppVersion(),
    };
};

const getBody = (isAnonymous) => {
    const {username, password} = authDataStoreService.getCredentials();

    if (!isAnonymous && !username) throw new Error('iccService: getBody, username is not provided');

    return {
        scope: 'offline_access profile pwa-user-api',
        grant_type: 'password',
        password: isAnonymous ? appConfig.getIccAnonymousToken() : password,
        username: isAnonymous ? 'anonymous' : username,
    };
};

const onIccApiMethodResponse = (response, isAnonymous) => {
    const responseData = authMapping(response.data);
    const {username} = authDataStoreService.getCredentials();

    authDataStoreService.setIccTokenData({...responseData, username, isAnonymous});

    return responseData;
};

const fetchIccToken = async (isAnonymous) => {
    const headers = getHeaders(isAnonymous);
    const data = urlUtils.stringify(getBody(isAnonymous));
    const response = await getToken({isAnonymous, data, headers});

    onIccApiMethodResponse(response, isAnonymous);

    log.info(`Request has successful response. Url: ${GET_TOKEN} isAnonymous: ${!!isAnonymous}`);
};

const refreshIccToken = async (isAnonymousUserAllowed) => {
    if (isAnonymousUserAllowed) {
        return await fetchIccToken(isAnonymousUserAllowed);
    }

    const isIccRefreshTokenEnabled = appConfig.getIsIccRefreshTokenEnabled();

    if (!isIccRefreshTokenEnabled) {
        log.info(`iccService: refreshIccToken, token refresh is disabled`, {logMessageType: logMessageTypes.ICC});

        authService.logout();
        return;
    }

    const refreshToken = authDataStoreService.getRefreshToken();
    const {username} = authDataStoreService.getCredentials();

    if (username && refreshToken) {
        const data = urlUtils.stringify({
            ...getBody(),
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
        });

        try {
            const headers = getHeaders();
            const response = await getToken({data, headers});
            const responseMapped = onIccApiMethodResponse(response);

            log.debug(`iccService: refreshIccToken, request: "${iccMethods.GET_TOKEN}" has successful response`, {
                logMessageType: logMessageTypes.ICC,
            });

            return responseMapped;
        } catch (e) {
            const errorStatus = e?.response?.status;

            storageService.removeAuthDataFromStorage();

            log.info(`iccService: refreshIccToken, request: "${iccMethods.GET_TOKEN}", error: ${e}`, {
                logMessageType: logMessageTypes.ICC,
            });

            if (errorStatus) {
                log.info(`iccService: refreshIccToken, BadRequest. Redirect to Login page`, {
                    logMessageType: logMessageTypes.ICC,
                });

                authService.logout();
            }

            throw e;
        }
    } else {
        log.info(
            `iccService: refreshIccToken, required auth data is empty, username: ${username}, refreshToken: ${refreshToken}`,
            {
                logMessageType: logMessageTypes.ICC,
            }
        );
        authService.logout();
        throw new Error('Required auth data is empty');
    }
};

const TRACKING = 'TRACKING';

const fetchUamToken = async ({isRetry, codentify} = {}) => {
    const state = getState();
    const currentUamToken = makeSelectUamToken(codentify)(state);

    if (currentUamToken) return currentUamToken;

    if (!productHelpers.isOriginalCodentifyValid(codentify)) {
        log.info(`iccService: fetchUamToken, codentify is not valid. codentify: ${codentify}`, {
            logMessageType: logMessageTypes.ICC,
        });
        return false;
    }

    const appVersion = appConfig.getAppVersion();
    const iccMethod = iccMethods.GET_UAM_TOKEN_V5;
    const message = `iccService: fetchUamToken, request: "${iccMethod}" for ${codentify}`;

    try {
        const gigyaToken = selectGigyaToken(state);
        const authToken = await authDataService.getAuthToken();
        const requestConfig = {
            headers: {Authorization: 'Bearer ' + authToken, v: appVersion, et: gigyaToken},
            data: {
                assets: [
                    {
                        id: codentify,
                        idtype: TRACKING,
                    },
                ],
            },
        };
        const response = await server.post(getIccApiUrl(iccMethod), {...requestConfig});
        const responseData = authUamMapping(response.data, codentify);

        dispatch(addUamToken({uamTokenResponse: responseData, codentify}));

        log.info(`${message} has successful response`, {logMessageType: logMessageTypes.ICC});
        return responseData;
    } catch (e) {
        log.error(`${message}, error: ${e}`, {logMessageType: logMessageTypes.ICC});
        if (isRetry) return false;

        await iccResponseErrorCheckService({status: e?.response?.status, error: e});
        return await fetchUamToken({isRetry: true, codentify});
    }
};

const fetchSSOLink = async (deeplinkId) => {
    const gigyaToken = selectGigyaToken(getState());
    const appVersion = appConfig.getAppVersion();
    const authToken = await authDataService.getAuthToken();
    const headers = {Authorization: 'Bearer ' + authToken, v: appVersion, et: gigyaToken};
    const {username} = storageService.getAuthDataFromStorage() || {};

    const data = {
        Login: username,
        AutoLoginLinkName: deeplinkId,
    };

    try {
        const response = await getLoginToken(data, headers);
        const result = loginTokenMapping(response.data);

        log.debug(
            `iccService: fetchSSOLink, request: "${iccMethods.GET_CONSUMER_LOGIN_TOKEN}" has successful response`,
            {
                logMessageType: logMessageTypes.ICC,
            }
        );

        return result;
    } catch (e) {
        const logMessage = `iccService: fetchSSOLink, request: "${iccMethods.GET_CONSUMER_LOGIN_TOKEN}", error: ${e}`;
        const errorStatus = e?.response?.status;

        logRequestError(errorStatus, logMessage);

        await iccResponseErrorCheckService({status: e?.response?.status, error: e, skipError: true});

        return {errorStatus};
    }
};

const fetchSSOLinks = async (linkNames, linkUrls, {includeSessionInfo = false} = {}) => {
    const gigyaToken = selectGigyaToken(getState());
    const appVersion = appConfig.getAppVersion();
    const authToken = await authDataService.getAuthToken();
    const headers = {Authorization: 'Bearer ' + authToken, v: appVersion, et: gigyaToken};
    const {username} = storageService.getAuthDataFromStorage() || {};

    const data = {
        Login: username,
        AutoLoginLinkNames: linkNames,
        AutoLoginLinkUrls: linkUrls,
    };

    try {
        const response = await getLoginTokens(data, headers, {IncludeSessionInfo: includeSessionInfo});
        const result = loginTokenListMapping(response.data);

        log.debug(
            `iccService: fetchSSOLink, request: "${iccMethods.GET_CONSUMER_LOGIN_TOKEN_LIST}" has successful response`,
            {
                logMessageType: logMessageTypes.ICC,
            }
        );

        return result;
    } catch (e) {
        const logMessage = `iccService: fetchSSOLink, request: "${iccMethods.GET_CONSUMER_LOGIN_TOKEN_LIST}", error: ${e}`;
        const errorStatus = e?.response?.status;

        logRequestError(errorStatus, logMessage);

        await iccResponseErrorCheckService({status: e?.response?.status, error: e, skipError: true});

        return {errorStatus};
    }
};

const verifyOtpCode = async ({code, token}) => {
    const headers = getHeaders();
    const body = {
        ...getBody(true),
        password: token,
        username: code,
    };
    const data = urlUtils.stringify(body);
    const response = await getToken({data, headers});

    onIccApiMethodResponse(response);

    return response;
};

const getToken = async ({isAnonymous, data, headers}) => {
    try {
        const result = await server.post(getIccApiUrl(iccMethods.GET_TOKEN), {data, headers});

        return result;
    } catch (e) {
        await iccResponseErrorCheckService({isAnonymous, status: e?.response?.status, error: e});

        throw e;
    }
};

const getLoginToken = (data, headers) =>
    server.post(getIccApiUrl(iccMethods.GET_CONSUMER_LOGIN_TOKEN), {data, headers});

const getLoginTokens = (data, headers, params) =>
    server.post(getIccApiUrl(iccMethods.GET_CONSUMER_LOGIN_TOKEN_LIST), {data, headers, params});

export default {
    fetchIccToken,
    refreshIccToken,
    fetchSSOLink,
    fetchSSOLinks,
    fetchUamToken,
    verifyOtpCode,
};
